import { Divider, Switch, Text } from '@/shared/ui/primitives';
import type { ReactElement } from 'react';
import { useRef } from 'react';
import { useController, useFormContext, type Validate } from 'react-hook-form';

import { TEST_IDS } from '@/testIds';
import { useUnlimitedToggle } from '@/shared/hooks';
import { EditableSectionCard } from '../EditableSectionCard';
import { FieldError } from '../FieldError';
import { NumberStepper } from './NumberStepper';
import styles from './CreditsStepper.module.css';

type CreditsFormShape = { credits: string; isUnlimitedCredits: boolean };

interface CreditsStepperProps {
  validate: Validate<string, CreditsFormShape>;
}

const MIN_CREDITS = 1;

const parseCredits = (raw: string): number => {
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const CreditsStepper = ({ validate }: CreditsStepperProps): ReactElement => {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext<CreditsFormShape>();

  const { isUnlimited, toggle } = useUnlimitedToggle<CreditsFormShape>({
    valueName: 'credits',
    toggleName: 'isUnlimitedCredits',
    getFallback: (last, get) => last || get('credits') || '',
  });

  const { field: creditsField } = useController({
    name: 'credits',
    control,
    rules: { validate },
  });

  const current = parseCredits(creditsField.value);
  const inputRef = useRef<HTMLInputElement>(null);
  const unlimitedToggleRef = useRef<HTMLInputElement>(null);

  const handleDelta = (delta: number): void => {
    if (isUnlimited) return;
    const next = Math.max(MIN_CREDITS, current + delta);
    creditsField.onChange(String(next));
    void trigger('credits');
  };

  return (
    <>
      <EditableSectionCard
        label="Credits"
        summary={isUnlimited ? 'Unlimited' : creditsField.value || '—'}
        focusTargetRef={isUnlimited ? unlimitedToggleRef : inputRef}
        invalid={Boolean(errors.credits)}
        triggerTestId={TEST_IDS.creditsEditorTrigger}
      >
        <NumberStepper
          value={creditsField.value}
          unlimited={isUnlimited}
          decrementDisabled={current <= MIN_CREDITS}
          invalid={Boolean(errors.credits)}
          onInput={(value) => creditsField.onChange(value)}
          onStep={handleDelta}
          onBlur={creditsField.onBlur}
          inputRef={inputRef}
          inputTestId={TEST_IDS.creditsInput}
          inputAriaLabel="Credits"
          decrementAriaLabel="Decrease credits"
          incrementAriaLabel="Increase credits"
          decrementTestId={TEST_IDS.passCreditsStepperDecrement}
          incrementTestId={TEST_IDS.passCreditsStepperIncrement}
        />

        <Divider />

        <div className={styles.toggleRow}>
          <Text weight="regular">Unlimited</Text>
          <Switch
            ref={unlimitedToggleRef}
            checked={isUnlimited}
            onChange={(e) => toggle(e.target.checked)}
            aria-label="Toggle unlimited credits"
            data-testid={TEST_IDS.passUnlimitedCreditsToggle}
          />
        </div>
      </EditableSectionCard>
      <FieldError message={errors.credits?.message?.toString()} />
    </>
  );
};
